import React from "react";
import { Select, Input, InputNumber, Button } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { formatCurrencyWithConversion } from "../../../utils/currencyUtils";

const { Option } = Select;

const getDecimalPrecision = () => {
  try { const s = localStorage.getItem("global_decimal_precision"); return s ? parseInt(s, 10) : 3; } catch { return 3; }
};

// Helper function to render hierarchical account options
// Note: Ant Design Select does not support nested OptGroup beyond one level.
// We therefore render top-level accounts as OptGroup and flatten deeper levels
// into indented Option items so that level 3/4 entries are visible/selectable.
const renderAccountOptions = (accounts) => {
  if (!accounts || !accounts.length) {
    return null;
  }

  // Build Option items for a subtree (no nested groups here)
  const buildIndentedOptions = (items, depth = 0, includeSelf = true) => {
    return items.flatMap((account) => {
      const hasChildren =
        Array.isArray(account.children) && account.children.length > 0;
      const indent = depth > 0 ? `${"\u2014 ".repeat(depth)}` : ""; // — repeated per depth

      const nodes = [];
      // Selectability: postable or leaf
      const selectable = !!(account.is_postable || !hasChildren);
      const label = account.code
        ? `${account.code} - ${account.name}`
        : account.name;

      // Include the node itself as an option (disabled if not selectable)
      if (includeSelf) {
        nodes.push(
          <Select.Option
            key={account.id}
            value={account.id}
            disabled={!selectable}
          >
            {`${indent}${label}`}
          </Select.Option>
        );
      }

      if (hasChildren) {
        nodes.push(...buildIndentedOptions(account.children, depth + 1, true));
      }
      return nodes;
    });
  };

  // Render only one OptGroup level (top-level accounts) to avoid nested groups
  return accounts.map((root) => (
    <Select.OptGroup key={root.id} label={`${root.code || ""} - ${root.name}`}>
      {/* Do not render the root as an option to avoid duplication with the group label */}
      {buildIndentedOptions([root], 0, false)}
    </Select.OptGroup>
  ));
};

/**
 * Creates invoice item columns configuration for the invoice items table
 * All fields now use direct state updates to bypass buggy handleItemChange utility
 */
const createInvoiceItemColumns = (
  products,
  categories,
  accounts,
  offers,
  handleProductChange,
  handleItemChange,
  getCurrency,
  setInvoiceItems,
  calculateTotals,
  removeItem,
  currency,
  mailingOffers,
  marketingFiles,
  invoiceItems,
  lists,
  segments,
  fetchSegments,
  navigate = null,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  paymentTerms = [],
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getIssueDate = null
) => {
  const columns = [
    {
      title: "Product Type",
      dataIndex: "productType",
      key: "productType",
      width: 120,
      render: (productType, record) => (
        <Select
          placeholder="Select type"
          style={{ width: "100%" }}
          value={productType || "product"}
          onChange={(value) => {
            // Direct state update
            setInvoiceItems((prevItems) => {
              const updatedItems = prevItems.map((item) => {
                if (item.key === record.key) {
                  // Clear fields based on new product type
                  const updatedItem = { ...item, productType: value };

                  // Reset fields that are specific to other product types
                  if (value === "product") {
                    delete updatedItem.printType;
                    delete updatedItem.envelope;
                    // Clear list specific fields
                    delete updatedItem.listId;
                    delete updatedItem.segmentId;
                  } else if (value === "print") {
                    // Clear product-specific fields
                    delete updatedItem.productId;
                    updatedItem.productName = "";
                    updatedItem.productVariation = "";
                    // Clear list specific fields
                    delete updatedItem.listId;
                    delete updatedItem.segmentId;
                  } else if (value === "list") {
                    // Clear both product and print specific fields
                    delete updatedItem.productId;
                    delete updatedItem.printType;
                    delete updatedItem.envelope;
                    updatedItem.productName = "";
                    updatedItem.productVariation = "";
                    // Initialize list specific fields
                    updatedItem.listId = null;
                    updatedItem.segmentId = null;
                  }

                  return updatedItem;
                }
                return item;
              });
              calculateTotals(updatedItems);
              return updatedItems;
            });
          }}
        >
          <Option value="general">General</Option>
          <Option value="product">Product</Option>
          <Option value="print">Print</Option>
          <Option value="list">List</Option>
        </Select>
      ),
    },
    {
      title: (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span>Category</span>
          <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            style={{ fontSize: "10px", padding: "0 6px", height: "20px" }}
            onClick={(e) => {
              e.stopPropagation();
              window.location.href = "/accounting/settings";
              localStorage.setItem("accounting_settings_active_tab", "2");
            }}
          >
            Add
          </Button>
        </div>
      ),
      dataIndex: "categoryId",
      key: "categoryId",
      width: 180,
      render: (categoryId, record) => {
        return (
          <Select
            placeholder="Select category"
            style={{ width: "100%" }}
            value={categoryId || undefined}
            allowClear
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.children ?? "")
                .toLowerCase()
                .includes(input.toLowerCase())
            }
            onChange={(value) => {
              setInvoiceItems((prevItems) => {
                const updatedItems = prevItems.map((item) =>
                  item.key === record.key
                    ? { ...item, categoryId: value || null }
                    : item
                );
                calculateTotals(updatedItems);
                return updatedItems;
              });
            }}
          >
            {categories.map((category) => (
              <Option key={category.id} value={category.id}>
                {category.name}
              </Option>
            ))}
          </Select>
        );
      },
    },

    {
      title: "Offer Code",
      dataIndex: "offerId",
      key: "offerId",
      width: 200,
      render: (offerId, record) => {
        // Only show for product type or if no product type is set (default to product)
        const productType = record.productType || "product";
        if (productType !== "product") {
          return null;
        }

        // Convert to number if it exists, otherwise null
        const normalizedOfferId = offerId ? Number(offerId) : null;

        return (
          <Select
            placeholder="Select offer code"
            style={{ width: "100%" }}
            allowClear
            showSearch
            optionFilterProp="children"
            value={normalizedOfferId}
            onChange={(selectedId) => {
              // Convert to number or null
              const id = selectedId ? Number(selectedId) : null;

              // Find the selected offer object
              const selectedOffer = id
                ? offers?.find((o) => Number(o.id) === id)
                : null;
              // DIRECT STATE UPDATE - Bypass handleItemChange completely
              setInvoiceItems((prevItems) => {
                const updatedItems = prevItems.map((item) => {
                  if (item.key === record.key) {
                    const updatedItem = { ...item, offerId: id };

                    if (selectedOffer) {
                      const productVariation =
                        Array.isArray(
                          selectedOffer.product_variations_detail
                        ) && selectedOffer.product_variations_detail.length > 0
                          ? selectedOffer.product_variations_detail[0]
                          : null;

                      // Extract product information
                      const productName =
                        productVariation?.name ||
                        selectedOffer.name ||
                        selectedOffer.product_name ||
                        "";
                      const variationDesc =
                        productVariation?.description ||
                        selectedOffer.product_variation ||
                        selectedOffer.variation ||
                        selectedOffer.description ||
                        "";

                      // Get price from offer with multiple fallbacks
                      const unitPrice =
                        selectedOffer.price ||
                        selectedOffer.unit_price ||
                        (productVariation && productVariation.price) ||
                        selectedOffer.product_price ||
                        selectedOffer.amount ||
                        0;

                      // Extract product ID from offer with multiple fallbacks
                      // IMPORTANT: Backend requires a valid product ID
                      const productId =
                        selectedOffer.product_id ||
                        selectedOffer.product ||
                        (productVariation && productVariation.product_id);

                      // Validate that the productId exists within the known products list
                      const isKnownProductId = !!(
                        productId &&
                        Array.isArray(products) &&
                        products.some((p) => Number(p.id) === Number(productId))
                      );

                      // Ensure quantity is at least 1
                      const quantity = updatedItem.quantity || 1;

                      // Calculate subtotal
                      const subtotal = Number(quantity) * Number(unitPrice);

                      // Update the item directly
                      updatedItem.productName = productName;
                      updatedItem.productVariation = variationDesc;
                      updatedItem.unitPrice = unitPrice;
                      updatedItem.subtotal = subtotal;
                      // Only set productId if it matches a known accounting product; otherwise, clear it
                      if (isKnownProductId) {
                        updatedItem.productId = Number(productId);
                      } else {
                        delete updatedItem.productId;
                      }
                    } else {
                      // Clear fields when no offer selected
                      updatedItem.productName = "";
                      updatedItem.productVariation = "";
                      // Don't clear price/subtotal to avoid breaking calculations
                      delete updatedItem.productId;
                    }

                    return updatedItem;
                  }
                  return item;
                });

                // Calculate totals with updated items
                calculateTotals(updatedItems);

                return updatedItems;
              });
            }}
          >
            {Array.isArray(offers) &&
              offers.map((offer) => (
                <Option key={offer.id} value={Number(offer.id)}>
                  {offer.code_offer ||
                    offer.code ||
                    offer.name ||
                    `Offer #${offer.id}`}
                </Option>
              ))}
          </Select>
        );
      },
    },

    // Print Type field - only shown when product type is 'print'
    {
      title: "Print Type",
      dataIndex: "printType",
      key: "printType",
      width: 150,
      render: (printType, record) => {
        const productType = record.productType || "product";
        if (productType !== "print") {
          return null;
        }

        return (
          <Select
            placeholder="Select Print Type"
            style={{ width: "100%" }}
            value={printType}
            onChange={(value) => {
              // Direct state update
              setInvoiceItems((prevItems) => {
                const updatedItems = prevItems.map((item) => {
                  if (item.key === record.key) {
                    const updatedItem = { ...item, printType: value };

                    // Reset envelope fields if changing from Envelope to Marketing
                    if (value === "Marketing") {
                      delete updatedItem.envelope;
                    }

                    return updatedItem;
                  }
                  return item;
                });
                calculateTotals(updatedItems);
                return updatedItems;
              });
            }}
            allowClear
          >
            <Option value="Marketing">Marketing</Option>
            <Option value="Envelope">Envelope</Option>
          </Select>
        );
      },
    },

    // Marketing Offer field - shown when product type is 'print'
    {
      title: "Marketing Offer",
      dataIndex: "marketingOffer",
      key: "marketingOffer",
      width: 200,
      render: (marketingOffer, record) => {
        const productType = record.productType || "product";

        if (productType !== "print") {
          return null;
        }

        return (
          <Select
            placeholder="Select Offer"
            style={{ width: "100%" }}
            value={marketingOffer}
            onChange={(value) => {
              // Direct state update
              setInvoiceItems((prevItems) => {
                const updatedItems = prevItems.map((item) => {
                  if (item.key === record.key) {
                    return { ...item, marketingOffer: value };
                  }
                  return item;
                });
                calculateTotals(updatedItems);
                return updatedItems;
              });
            }}
          >
            {Array.isArray(mailingOffers) &&
              mailingOffers.map((offer) => (
                <Option key={offer.id} value={offer.id}>
                  {offer.code_offer}
                </Option>
              ))}
          </Select>
        );
      },
    },

    // Marketing File field - shown when product type is 'print'
    {
      title: "Marketing File",
      dataIndex: "envelope",
      key: "envelope",
      width: 200,
      render: (envelope, record) => {
        const productType = record.productType || "product";

        if (productType !== "print") {
          return null;
        }

        return (
          <Select
            placeholder="Select Marketing File"
            style={{ width: "100%" }}
            value={envelope}
            onChange={(value) => {
              // Direct state update
              setInvoiceItems((prevItems) => {
                const updatedItems = prevItems.map((item) => {
                  if (item.key === record.key) {
                    // Ensure we're storing the ID value
                    return { ...item, envelope: value };
                  }
                  return item;
                });
                calculateTotals(updatedItems);
                return updatedItems;
              });
            }}
          >
            {Array.isArray(marketingFiles) &&
              marketingFiles.map((file) => (
                <Option key={file.id} value={file.id}>
                  {file.file_name ||
                    file.name ||
                    file.original_filename ||
                    `File #${file.id}`}
                </Option>
              ))}
          </Select>
        );
      },
    },

    {
      title: "Product Name",
      dataIndex: "productName",
      key: "productName",
      render: (productName, record) => {
        // Only show for product type; hide for print and list
        const productType = record.productType || "product";
        if (productType !== "product") {
          return null;
        }

        return (
          <Input
            placeholder="Enter product name"
            value={productName}
            onChange={(e) => {
              // DIRECT STATE UPDATE - Bypass handleItemChange completely
              setInvoiceItems((prevItems) => {
                const updatedItems = prevItems.map((item) => {
                  if (item.key === record.key) {
                    return { ...item, productName: e.target.value };
                  }
                  return item;
                });
                return updatedItems;
              });
            }}
          />
        );
      },
    },

    {
      title: "List",
      dataIndex: "listId",
      key: "listId",
      render: (text, record) => (
        <Select
          showSearch
          value={text}
          onChange={(value) => {
            handleItemChange(record.key, "listId", value);
            fetchSegments(value);
          }}
          placeholder="Select a list"
          optionFilterProp="children"
          filterOption={(input, option) =>
            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
        >
          {lists.map((list) => (
            <Select.Option key={list.id} value={list.id}>
              {list.name}
            </Select.Option>
          ))}
        </Select>
      ),
    },

    {
      title: "Segment",
      dataIndex: "segmentId",
      key: "segmentId",
      render: (text, record) => (
        <Select
          showSearch
          value={text}
          onChange={(value) => handleItemChange(record.key, "segmentId", value)}
          placeholder="Select a segment"
          optionFilterProp="children"
          filterOption={(input, option) =>
            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
          disabled={!record.listId} // Disable if no list is selected
        >
          {segments.map((segment) => (
            <Select.Option key={segment.id} value={segment.id}>
              {segment.name}
            </Select.Option>
          ))}
        </Select>
      ),
    },

    {
      title: "Product Variation",
      dataIndex: "productVariation",
      key: "productVariation",
      render: (productVariation, record) => {
        // Only show for product type
        const productType = record.productType || "product";
        if (productType !== "product") {
          return null;
        }

        return (
          <Input
            placeholder="Enter product variation"
            value={productVariation}
            onChange={(e) => {
              // Direct state update bypassing handleItemChange
              setInvoiceItems((prevItems) => {
                const updatedItems = prevItems.map((item) =>
                  item.key === record.key
                    ? { ...item, productVariation: e.target.value }
                    : item
                );
                calculateTotals(updatedItems);
                return updatedItems;
              });
            }}
          />
        );
      },
    },

    {
      title: (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span>Chart of Account</span>
          <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            style={{ fontSize: "10px", padding: "0 6px", height: "20px" }}
            onClick={(e) => {
              e.stopPropagation();
              // Navigate to Advance Accounting with Chart of Accounts tab
              localStorage.setItem(
                "advance_accounting_active_tab",
                "chart-of-accounts"
              );
              if (navigate) {
                navigate("/accounting/advance-accounting");
              } else {
                window.location.href = "/accounting/advance-accounting";
              }
            }}
          >
            Add
          </Button>
        </div>
      ),
      dataIndex: "accountId",
      key: "accountId",
      width: 200,
      render: (accountId, record) => (
        <Select
          placeholder="Select account"
          style={{ width: "100%" }}
          value={accountId}
          allowClear
          dropdownStyle={{ minWidth: 400 }}
          onChange={(value) => {
            // Direct state update bypassing handleItemChange
            setInvoiceItems((prevItems) => {
              const updatedItems = prevItems.map((item) =>
                item.key === record.key ? { ...item, accountId: value } : item
              );
              calculateTotals(updatedItems);
              return updatedItems;
            });
          }}
          showSearch
          optionFilterProp="children"
        >
          {accounts && renderAccountOptions(accounts)}
        </Select>
      ),
    },

    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: 200,
      render: (description, record) => (
        <Input
          value={description || ""}
          placeholder="Description (required)"
          required
          size="middle"
          onChange={(e) => {
            // Direct state update bypassing handleItemChange
            setInvoiceItems((prevItems) => {
              const updatedItems = prevItems.map((item) =>
                item.key === record.key
                  ? { ...item, description: e.target.value }
                  : item
              );
              calculateTotals(updatedItems);
              return updatedItems;
            });
          }}
          status={!description || description.trim() === "" ? "error" : ""}
        />
      ),
    },

    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      width: 120,
      render: (quantity, record) => (
        <InputNumber
          min={1}
          precision={0}
          value={quantity}
          size="middle"
          style={{ width: "100%" }}
          formatter={(value) => {
            if (!value) return "";
            return `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
          }}
          parser={(value) => {
            if (!value) return "";
            return value.replace(/,/g, "");
          }}
          onChange={(value) => {
            // Direct state update bypassing handleItemChange
            setInvoiceItems((prevItems) => {
              const updatedItems = prevItems.map((item) => {
                if (item.key === record.key) {
                  const updatedItem = { ...item, quantity: value };
                  // Calculate subtotal when quantity changes
                  const unitPrice = Number(updatedItem.unitPrice || 0);
                  const quantity = Number(value || 0);
                  updatedItem.subtotal = unitPrice * quantity;
                  return updatedItem;
                }
                return item;
              });
              calculateTotals(updatedItems);
              return updatedItems;
            });
          }}
        />
      ),
    },

    {
      title: "Unit Price",
      dataIndex: "unitPrice",
      key: "unitPrice",
      width: 180,
      render: (unitPrice, record) => (
        <Input
          addonBefore={currency}
          value={unitPrice}
          placeholder={`0.${"0".repeat(getDecimalPrecision())}`}
          size="middle"
          onPaste={(e) => {
            // Allow paste and process the pasted value
            e.preventDefault();
            const pastedText = (
              e.clipboardData || window.clipboardData
            ).getData("text");
            // Validate pasted value matches number format
            if (pastedText === "" || /^-?\d*\.?\d*$/.test(pastedText)) {
              const inputValue = pastedText;
              setInvoiceItems((prevItems) => {
                const updatedItems = prevItems.map((item) => {
                  if (item.key === record.key) {
                    const updatedItem = { ...item, unitPrice: inputValue };
                    const unitPriceNum = inputValue
                      ? parseFloat(inputValue) || 0
                      : 0;
                    const quantity = Number(updatedItem.quantity || 0);
                    updatedItem.subtotal = unitPriceNum * quantity;
                    return updatedItem;
                  }
                  return item;
                });
                calculateTotals(updatedItems);
                return updatedItems;
              });
            }
          }}
          onChange={(e) => {
            const inputValue = e.target.value;
            // Allow empty, numbers, and decimal points while typing
            // This regex allows: empty string, numbers, numbers with decimal point, numbers with decimal and digits after
            if (inputValue === "" || /^-?\d*\.?\d*$/.test(inputValue)) {
              setInvoiceItems((prevItems) => {
                const updatedItems = prevItems.map((item) => {
                  if (item.key === record.key) {
                    // Store the raw string value to preserve decimal point while typing
                    const updatedItem = { ...item, unitPrice: inputValue };
                    // Calculate subtotal - parse the value for calculation
                    const unitPriceNum = inputValue
                      ? parseFloat(inputValue) || 0
                      : 0;
                    const quantity = Number(updatedItem.quantity || 0);
                    updatedItem.subtotal = unitPriceNum * quantity;
                    return updatedItem;
                  }
                  return item;
                });
                calculateTotals(updatedItems);
                return updatedItems;
              });
            }
          }}
          onBlur={(e) => {
            // On blur, format to 3 decimal places if it's a valid number
            const value = e.target.value;
            if (value && !isNaN(parseFloat(value))) {
              const formattedValue = parseFloat(value).toFixed(getDecimalPrecision());
              setInvoiceItems((prevItems) => {
                const updatedItems = prevItems.map((item) => {
                  if (item.key === record.key) {
                    const updatedItem = { ...item, unitPrice: formattedValue };
                    const unitPriceNum = parseFloat(formattedValue) || 0;
                    const quantity = Number(updatedItem.quantity || 0);
                    updatedItem.subtotal = unitPriceNum * quantity;
                    return updatedItem;
                  }
                  return item;
                });
                calculateTotals(updatedItems);
                return updatedItems;
              });
            }
          }}
          style={{ width: "100%", minWidth: 100 }}
        />
      ),
    },

    {
      title: "Subtotal",
      dataIndex: "subtotal",
      key: "subtotal",
      width: 150,
      render: (subtotal, record) => {
        // Get currency dynamically - use getCurrency function if available, otherwise use currency parameter
        const itemCurrency =
          typeof getCurrency === "function"
            ? getCurrency()
            : record.currency || currency || "$";
        return formatCurrencyWithConversion(subtotal, itemCurrency);
      },
    },

    {
      title: "Actions",
      key: "actions",
      width: 80,
      render: (_, record) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => removeItem(record.key)}
        />
      ),
    },
  ];

  // Determine which columns are relevant based on current items.
  // If no items yet, keep all columns visible (better UX on initial render).
  const hasItems = Array.isArray(invoiceItems) && invoiceItems.length > 0;
  const hasProductItems =
    !hasItems ||
    invoiceItems.some((it) => (it.productType || "product") === "product");
  const hasPrintItems =
    !hasItems || invoiceItems.some((it) => it.productType === "print");
  const hasListItems =
    !hasItems || invoiceItems.some((it) => it.productType === "list");
  const hasPrintMarketing =
    !hasItems || invoiceItems.some((it) => it.productType === "print");
  const hasPrintEnvelope =
    !hasItems || invoiceItems.some((it) => it.productType === "print");

  const filteredColumns = columns.filter((col) => {
    switch (col.key) {
      case "offerId":
        return hasProductItems;
      case "printType":
        return hasPrintItems;
      case "marketingOffer":
        return hasPrintMarketing;
      case "envelope":
        return hasPrintEnvelope;
      case "productName":
        return hasProductItems; // hide when only list items
      case "listId":
        return hasListItems;
      case "segmentId":
        return hasListItems;
      case "productVariation":
        return hasProductItems;
      default:
        return true;
    }
  });

  return filteredColumns;
};

export default createInvoiceItemColumns;
